import { useState } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Menu, MenuItem } from '@mui/material';
import Button from 'domains/core/components/Button';
import './TableOffsetPagination.css';

export type TableOffsetPaginationProps = {
    /** Current page (1-based). */
    page: number;
    /** Total number of pages (at least 1 for display). */
    totalPages: number;
    onPreviousPage: () => void;
    onNextPage: () => void;
    /** Rows per page (shown in UI; parent must use for API `limit`). */
    rowsPerPage: number;
    /** Optional: when set with onRowsPerPageChange, shows a dropdown to change page size. */
    rowsPerPageOptions?: number[];
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    /** Optional prefix for data-testid / aria labels. */
    testIdPrefix?: string;
};

/**
 * Reusable footer pagination for offset/limit APIs (page, totalPages, prev/next).
 * Optionally supports a rows-per-page dropdown when rowsPerPageOptions and onRowsPerPageChange are provided.
 */
const TableOffsetPagination = ({
    page,
    totalPages,
    onPreviousPage,
    onNextPage,
    rowsPerPage,
    rowsPerPageOptions,
    onRowsPerPageChange,
    testIdPrefix = 'table-offset-pagination',
}: TableOffsetPaginationProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSelectRowsPerPage = (event: React.MouseEvent<HTMLLIElement>) => {
        const raw = event.currentTarget.dataset.myValue;
        if (raw && onRowsPerPageChange) {
            onRowsPerPageChange(parseInt(raw, 10));
        }
        setAnchorEl(null);
    };

    const showDropdown = Boolean(rowsPerPageOptions?.length && onRowsPerPageChange);

    return (
        <div className="table-offset-pagination" data-testid={`${testIdPrefix}-container`}>
            <div className="table-offset-pagination__rows">
                <div>rows per page:</div>
                {showDropdown ? (
                    <div className="table-offset-pagination__rows-dropdown">
                        <Button
                            aria-controls={`${testIdPrefix}-rows-menu`}
                            aria-haspopup="true"
                            aria-label="Rows per page"
                            onClick={handleOpenMenu}
                            data-testid={`${testIdPrefix}-rows-button`}
                        >
                            {rowsPerPage}
                        </Button>
                        <Menu
                            id={`${testIdPrefix}-rows-menu`}
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                            sx={{ marginLeft: '.5rem' }}
                        >
                            {rowsPerPageOptions!.map((n) => (
                                <MenuItem
                                    onClick={handleSelectRowsPerPage}
                                    key={n}
                                    data-my-value={n}
                                    value={n}
                                    sx={{
                                        paddingLeft: '.5rem',
                                        paddingRight: '.5rem',
                                        textAlign: 'center',
                                        width: '3rem',
                                    }}
                                >
                                    <span className="table-offset-pagination__rows-per-page-dropdown-text">{n}</span>
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                ) : (
                    <div className="table-offset-pagination__rows-value" data-testid={`${testIdPrefix}-rows-fixed`}>
                        {rowsPerPage}
                    </div>
                )}
            </div>
            <div className="table-offset-pagination__page-number" data-testid={`${testIdPrefix}-page-label`}>
                page {page} of {totalPages}
            </div>
            <div className="table-offset-pagination__nav">
                <Button
                    className="table-offset-pagination__nav-button"
                    sx={{ minWidth: '40px', maxWidth: '40px' }}
                    onClick={onPreviousPage}
                    disabled={page <= 1}
                    aria-label="Previous page"
                    data-testid={`${testIdPrefix}-prev`}
                >
                    <ChevronLeftIcon fontSize="small" />
                </Button>
                <Button
                    className="table-offset-pagination__nav-button"
                    sx={{ minWidth: '40px', maxWidth: '40px' }}
                    onClick={onNextPage}
                    disabled={page >= totalPages}
                    aria-label="Next page"
                    data-testid={`${testIdPrefix}-next`}
                >
                    <ChevronRightIcon fontSize="small" />
                </Button>
            </div>
        </div>
    );
};

export default TableOffsetPagination;
